import { z } from "zod";
import { UserRoleEnum } from "../enums/userRole.enum";

export const registerSchema = z.object({
  displayName: z.string().min(1, { message: "Name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  password: z
    .string()
    .min(12, { message: "Password must be at least 12 characters" })
    .regex(/[A-Z]/, { message: "Must contain an uppercase letter" })
    .regex(/[a-z]/, { message: "Must contain a lowercase letter" })
    .regex(/[0-9]/, { message: "Must contain a digit" })
    .regex(/[^A-Za-z0-9]/, { message: "Must contain a symbol" }),
  userRole: z.nativeEnum(UserRoleEnum),
});

export type RegisterFormData = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string().min(1, { message: "Password is required" }),
});

export type LoginFormData = z.infer<typeof loginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, { message: "Current password is required" }),
  newPassword: z
    .string()
    .min(12, { message: "Password must be at least 12 characters" })
    .regex(/[A-Z]/, { message: "Must contain an uppercase letter" })
    .regex(/[a-z]/, { message: "Must contain a lowercase letter" })
    .regex(/[0-9]/, { message: "Must contain a digit" })
    .regex(/[^A-Za-z0-9]/, { message: "Must contain a symbol" }),
  confirmPassword: z.string().min(1, { message: "Please confirm your new password" }),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export const contactSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  message: z
    .string()
    .min(1, { message: "Message is required" })
    .max(1000, { message: "Message must be 1000 characters or fewer" }),
});

export type ContactFormData = z.infer<typeof contactSchema>;

export const jobSchema = z.object({
  id: z.number(),
  title: z.string().min(1, { message: "Title is required" }),
  jobCategoryId: z.number().refine((val) => val !== 0, {
    message: "Job category is required",
  }),
  locationRegionId: z.number().refine((val) => val !== 0, {
    message: "Region is required",
  }),
  locationId: z.number().refine((val) => val !== 0, {
    message: "Location is required",
  }),
  description: z.string().min(1, { message: "Description is required" }),
  companyName: z.string().min(1, { message: "Company name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  phone: z
    .string()
    .min(7, { message: "Phone number must be at least 7 digits" })
    .regex(/^\+?\d{7,15}$/, { message: "Invalid phone number format" })
    .nullable(),
  effectiveFrom: z.string().min(1, { message: "Effective date is required" }),
  salary: z.number().nullable(),
});

export type JobFormData = z.infer<typeof jobSchema>;

export const blogSchema = z.object({
  id: z.number(),
  blogCategoryId: z.number().nullable().optional(),
  title: z.string().min(1, { message: "Title is required" }),
  slug: z
    .string()
    .min(1, { message: "Slug is required" })
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
      message: "Use lowercase letters, numbers, and hyphens only",
    }),
  summary: z.string().max(1000, { message: "Summary must be 1000 characters or fewer" }).nullable().optional(),
  contentHtml: z.string().min(1, { message: "Content is required" }),
  blogPostStatusId: z.number(),
  publishedAt: z.string().nullable().optional(),
  readTimeMinutes: z.number().nullable().optional(),
  seoTitle: z.string().max(250, { message: "SEO title must be 250 characters or fewer" }).nullable().optional(),
  seoDescription: z.string().max(320, { message: "SEO description must be 320 characters or fewer" }).nullable().optional(),
  isFeatured: z.boolean(),
  galleryImages: z.array(
    z.object({
      id: z.number(),
      blogPostId: z.number(),
      imageUrl: z.string().url(),
      blobName: z.string().nullable().optional(),
      altText: z.string().max(250, { message: "Alt text must be 250 characters or fewer" }).nullable().optional(),
      caption: z.string().max(500, { message: "Caption must be 500 characters or fewer" }).nullable().optional(),
      photographerName: z.string().max(150, { message: "Photographer name must be 150 characters or fewer" }).nullable().optional(),
      photographerUrl: z.union([z.string().url(), z.literal(""), z.null()]).optional(),
      sourceName: z.string().max(150, { message: "Source name must be 150 characters or fewer" }).nullable().optional(),
      sourceUrl: z.union([z.string().url(), z.literal(""), z.null()]).optional(),
      locationName: z.string().max(200, { message: "Location must be 200 characters or fewer" }).nullable().optional(),
      sortOrder: z.number(),
      isCover: z.boolean(),
    })
  ),
});

export type BlogFormData = z.infer<typeof blogSchema>;
